import React, { useEffect, useState } from 'react';
import { useTalentos } from '../../../../application/talentos/hooks/useTalentos';
import ToolbarComponent from '../../../ui/section';
import { Row, Col } from '../../../ui/grid';
import Loader from '../../../ui/loader';
import Input from '../../../ui/input';
import Select from '../../../ui/InputSelect';
import Modal from '../../../ui/modal2';
import './statistics.css';

const DATE_PRESETS = [
    { id: '7d', label: 'Últimos 7 días', days: 7 },
    { id: '14d', label: 'Últimos 14 días', days: 14 },
    { id: '1m', label: 'Último mes', days: 30 },
    { id: '3m', label: 'Últimos 3 meses', days: 90 },
    { id: '6m', label: 'Últimos 6 meses', days: 180 },
    { id: '1y', label: 'Último año', days: 365 },
    { id: 'custom', label: 'Personalizado', days: null }
];

const getDateRangeFromPreset = (presetId) => {
    const preset = DATE_PRESETS.find(p => p.id === presetId);
    if (!preset || preset.id === 'custom') return null;
    
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - preset.days);
    
    return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
    };
};

const StatisticsPage = () => {
    const { 
        isLoading, 
        error,
        getKPIs,
        getDashboardSummary,
        getGrowthStatistics,
        getCategoryStatistics,
        getGeographicStatistics,
        getApplicationStatistics,
        getCompanies,
        getEmployers,
        getApplications
    } = useTalentos();
    
    const [kpis, setKpis] = useState(null);
    const [summary, setSummary] = useState(null);
    const [categories, setCategories] = useState(null);
    const [geography, setGeography] = useState(null);
    const [companyGeography, setCompanyGeography] = useState(null);
    const [employerGeography, setEmployerGeography] = useState(null);
    const [applicationStats, setApplicationStats] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const getDefaultDateRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);
        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };
    };

    const [dateRange, setDateRange] = useState(getDefaultDateRange());
    const [selectedPreset, setSelectedPreset] = useState('1m'); // Por defecto último mes
    const [isCustomMode, setIsCustomMode] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(''); // Filtro por país
    const [availableCountries, setAvailableCountries] = useState([]); // Lista de países disponibles
    const [showApplicationsModal, setShowApplicationsModal] = useState(false);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [modalTitle, setModalTitle] = useState('');
    const [loadingApplications, setLoadingApplications] = useState(false);

    const normalizeGeography = (raw) => {
        if (!raw) return null;

        // Algunos endpoints devuelven { data: ... }
        const data = (raw && typeof raw === 'object' && raw.data) ? raw.data : raw;

        const asTopList = (obj, kind) => {
            if (!obj || typeof obj !== 'object') return [];
            return Object.entries(obj)
                .map(([name, count]) => ({
                    name,
                    [kind]: name,
                    count: typeof count === 'number' ? count : (count?.count ?? count?.total ?? 0)
                }))
                .sort((a, b) => (b.count || 0) - (a.count || 0));
        };

        const pickList = (value, kind) => {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            if (typeof value === 'object') return asTopList(value, kind);
            return [];
        };

        // Intentar cubrir varios formatos conocidos/posibles
        const countries =
            pickList(data.countries, 'country') ||
            pickList(data.byCountry, 'country') ||
            pickList(data.country, 'country') ||
            pickList(data.countriesDistribution, 'country') ||
            [];

        const cities =
            pickList(data.cities, 'city') ||
            pickList(data.byCity, 'city') ||
            pickList(data.city, 'city') ||
            pickList(data.citiesDistribution, 'city') ||
            [];

        // Si viene como lista plana con {country/city, count}
        const flat = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : null);
        const flatCountries = Array.isArray(flat) ? flat.filter(i => i?.country) : [];
        const flatCities = Array.isArray(flat) ? flat.filter(i => i?.city) : [];

        const normalized = {
            countries: (Array.isArray(countries) && countries.length ? countries : flatCountries),
            cities: (Array.isArray(cities) && cities.length ? cities : flatCities)
        };

        // Si no hay nada, igual devolvemos objeto para mostrar mensaje “sin datos”
        return normalized;
    };

    const buildGeographyFromEntities = (entities = []) => {
        const safe = Array.isArray(entities) ? entities : [];

        const getFirstString = (...values) => {
            for (const v of values) {
                if (typeof v === 'string' && v.trim()) return v.trim();
            }
            return null;
        };

        const extractCountryCity = (e) => {
            if (!e || typeof e !== 'object') return { country: null, city: null };
            const loc = e.location || e.address || e.company?.location || e.company?.address || null;

            const country = getFirstString(
                e.country,
                e.countryName,
                loc?.country,
                loc?.countryName,
                loc?.country_code,
                loc?.countryCode
            );

            const city = getFirstString(
                e.city,
                e.cityName,
                loc?.city,
                loc?.cityName,
                loc?.town
            );

            return { country, city };
        };

        const countBy = (items, key) => {
            const map = new Map();
            for (const it of items) {
                const v = it[key];
                if (!v) continue;
                map.set(v, (map.get(v) || 0) + 1);
            }
            return Array.from(map.entries())
                .map(([name, count]) => ({ name, [key]: name, count }))
                .sort((a, b) => b.count - a.count);
        };

        const rows = safe.map(extractCountryCity);
        return {
            countries: countBy(rows, 'country'),
            cities: countBy(rows, 'city')
        };
    };

    useEffect(() => {
        loadStatistics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange.startDate, dateRange.endDate, selectedCountry]);

    const loadStatistics = async () => {
        setLoading(true);
        try {
            const filters = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                ...(selectedCountry && { country: selectedCountry }) // Agregar filtro de país si está seleccionado
            };

            const [kpisData, summaryData, categoriesData, geographyData, applicationsData, companiesData, employersData] = await Promise.all([
                getKPIs(filters).catch(() => null),
                getDashboardSummary(filters).catch(() => null),
                getCategoryStatistics(filters).catch(() => null),
                getGeographicStatistics(filters).catch(() => null),
                getApplicationStatistics(filters).catch(() => null),
                // Para no saturar, pedimos un lote grande. Si el backend pagina distinto, igual se mostrará con lo que devuelva.
                getCompanies({ limit: 1000, ...(selectedCountry && { country: selectedCountry }) }).catch(() => []),
                getEmployers({ limit: 1000, ...(selectedCountry && { country: selectedCountry }) }).catch(() => [])
            ]);

            setKpis(kpisData);
            setSummary(summaryData);
            setCategories(categoriesData);
            const normalizedGeography = normalizeGeography(geographyData);
            setGeography(normalizedGeography);
            setApplicationStats(applicationsData);

            // Actualizar lista de países disponibles desde los datos geográficos
            if (normalizedGeography && Array.isArray(normalizedGeography.countries)) {
                const countries = normalizedGeography.countries.map(c => c.country || c.name).filter(Boolean);
                // También agregar países de compañías y empleadores
                const allCountries = new Set(countries);
                if (Array.isArray(companiesData)) {
                    companiesData.forEach(company => {
                        const country = company.country || company.countryName || company.location?.country || company.location?.countryName || company.address?.country || company.address?.countryName;
                        if (country) allCountries.add(country);
                    });
                }
                if (Array.isArray(employersData)) {
                    employersData.forEach(employer => {
                        const country = employer.country || employer.countryName || employer.location?.country || employer.location?.countryName || employer.address?.country || employer.address?.countryName;
                        if (country) allCountries.add(country);
                    });
                }
                setAvailableCountries([...allCountries].sort());
            }

            // Filtrar compañías y empleadores por país si está seleccionado
            let filteredCompanies = companiesData;
            let filteredEmployers = employersData;
            
            if (selectedCountry) {
                filteredCompanies = (companiesData || []).filter(company => {
                    const country = company.country || 
                                   company.countryName || 
                                   company.location?.country || 
                                   company.location?.countryName ||
                                   company.address?.country ||
                                   company.address?.countryName;
                    return country === selectedCountry;
                });
                
                filteredEmployers = (employersData || []).filter(employer => {
                    const country = employer.country || 
                                   employer.countryName || 
                                   employer.location?.country || 
                                   employer.location?.countryName ||
                                   employer.address?.country ||
                                   employer.address?.countryName;
                    return country === selectedCountry;
                });
            }
            
            setCompanyGeography(buildGeographyFromEntities(filteredCompanies));
            setEmployerGeography(buildGeographyFromEntities(filteredEmployers));
        } catch (err) {
            console.error('Error al cargar estadísticas:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
        // Si se cambia manualmente, activar modo personalizado
        if (selectedPreset !== 'custom') {
            setSelectedPreset('custom');
            setIsCustomMode(true);
        }
    };

    const handlePresetSelect = (presetId) => {
        setSelectedPreset(presetId);
        if (presetId === 'custom') {
            setIsCustomMode(true);
        } else {
            setIsCustomMode(false);
            const newRange = getDateRangeFromPreset(presetId);
            if (newRange) {
                setDateRange(newRange);
            }
        }
    };

    const handleReset = () => {
        const defaultRange = getDefaultDateRange();
        setDateRange(defaultRange);
        setSelectedPreset('1m'); // Último mes como default
        setIsCustomMode(false);
        setSelectedCountry(''); // Resetear también el filtro de país
    };

    const formatDateRange = () => {
        if (!dateRange.startDate || !dateRange.endDate) return '';
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        const options = { day: 'numeric', month: 'short', year: 'numeric' };
        return `${start.toLocaleDateString('es-ES', options)} - ${end.toLocaleDateString('es-ES', options)}`;
    };

    const handleRefresh = () => {
        loadStatistics();
    };

    // Funciones de traducción
    const translateStage = (stage) => {
        const translations = {
            'received': 'Recibida',
            'in_review': 'En revisión',
            'interview': 'Entrevista',
            'offer': 'Oferta',
            'hired': 'Contratado',
            'rejected': 'Rechazado'
        };
        return translations[stage] || stage;
    };

    const translateStatus = (status) => {
        const translations = {
            'active': 'Activa',
            'withdrawn': 'Retirada',
            'closed': 'Cerrada'
        };
        return translations[status] || status;
    };

    const getStageColor = (stage) => {
        const colors = {
            received: '#6B7280',
            in_review: '#3B82F6',
            interview: '#8B5CF6',
            offer: '#10B981',
            hired: '#059669',
            rejected: '#EF4444'
        };
        return colors[stage] || '#6B7280';
    };

    const getStatusColor = (status) => {
        const colors = {
            active: '#10B981',
            withdrawn: '#F59E0B',
            closed: '#6B7280'
        };
        return colors[status] || '#6B7280';
    };

    const handleViewApplications = async (filterType, filterValue, displayName) => {
        setLoadingApplications(true);
        setModalTitle(`${displayName} (${filterType === 'stage' ? 'Etapa' : 'Estado'})`);
        setShowApplicationsModal(true);
        
        try {
            const filters = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                ...(selectedCountry && { country: selectedCountry }),
                ...(filterType === 'stage' ? { stage: filterValue } : { status: filterValue })
            };
            
            const applications = await getApplications(filters);
            console.log('Aplicaciones recibidas:', applications);
            console.log('Filtros aplicados:', filters);
            setFilteredApplications(Array.isArray(applications) ? applications : []);
        } catch (err) {
            console.error('Error al cargar aplicaciones:', err);
            setFilteredApplications([]);
        } finally {
            setLoadingApplications(false);
        }
    };

    const formatApplicationDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading || isLoading) {
        return (
            <ToolbarComponent label="Estadísticas" showBackButton={false}>
                <Loader text="Cargando estadísticas..." />
            </ToolbarComponent>
        );
    }

    const kpisData = kpis || {};
    const usersData = kpisData.users || {};
    const jobsData = kpisData.jobs || {};
    const practicesData = kpisData.practices || {};
    const applicationsData = kpisData.applications || {};

    return (
        <ToolbarComponent label="Estadísticas" showBackButton={false}>
            <div className="statistics-container">
                {error && (
                    <div style={{ 
                        padding: '12px 16px', 
                        background: '#FEF2F2', 
                        color: '#DC2626', 
                        borderRadius: '6px', 
                        marginBottom: '24px',
                        fontSize: '14px'
                    }}>
                        {error}
                    </div>
                )}

                {/* Filtros mejorados */}
                <div className="date-filter-container">
                    <div className="date-filter-header">
                        <div className="date-filter-label">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2.66667H11.3333V2C11.3333 1.63181 11.0349 1.33333 10.6667 1.33333C10.2985 1.33333 10 1.63181 10 2V2.66667H6V2C6 1.63181 5.70152 1.33333 5.33333 1.33333C4.96514 1.33333 4.66667 1.63181 4.66667 2V2.66667H4C2.89543 2.66667 2 3.5621 2 4.66667V12C2 13.1046 2.89543 14 4 14H12C13.1046 14 14 13.1046 14 12V4.66667C14 3.5621 13.1046 2.66667 12 2.66667ZM12.6667 12C12.6667 12.3682 12.3682 12.6667 12 12.6667H4C3.63181 12.6667 3.33333 12.3682 3.33333 12V6.66667H12.6667V12Z" fill="currentColor"/>
                            </svg>
                            <span>Filtros</span>
                        </div>
                        <div className="date-filter-actions">
                            {dateRange.startDate && dateRange.endDate && (
                                <div className="date-range-display">
                                    {formatDateRange()}
                                </div>
                            )}
                            <button 
                                className="date-reset-btn"
                                onClick={handleReset}
                                title="Restablecer filtros"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 2.66667C5.05448 2.66667 2.66667 5.05448 2.66667 8C2.66667 10.9455 5.05448 13.3333 8 13.3333C10.9455 13.3333 13.3333 10.9455 13.3333 8C13.3333 5.05448 10.9455 2.66667 8 2.66667ZM8 12C6.15905 12 4.66667 10.5076 4.66667 8.66667C4.66667 6.82571 6.15905 5.33333 8 5.33333C9.84095 5.33333 11.3333 6.82571 11.3333 8.66667C11.3333 10.5076 9.84095 12 8 12Z" fill="currentColor"/>
                                    <path d="M6.66667 6.66667L5.33333 8L6.66667 9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M9.33333 6.66667L10.6667 8L9.33333 9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>Resetear</span>
                            </button>
                        </div>
                    </div>
                    
                    {/* Filtro por país */}
                    <div style={{ marginBottom: '16px', marginTop: '16px' }}>
                        <Select
                            label="Filtrar por país"
                            options={[
                                { value: '', label: 'Todos los países' },
                                ...availableCountries.map(country => ({
                                    value: country,
                                    label: country
                                }))
                            ]}
                            value={selectedCountry ? { value: selectedCountry, label: selectedCountry } : null}
                            onChange={(option) => setSelectedCountry(option ? option.value : '')}
                            placeholder="Selecciona un país..."
                        />
                    </div>
                    
                    <div className="date-presets">
                        {DATE_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                className={`date-preset-btn ${selectedPreset === preset.id ? 'active' : ''}`}
                                onClick={() => handlePresetSelect(preset.id)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                    
                    <div className={`date-custom-container ${isCustomMode ? 'expanded' : ''}`}>
                        <div className="date-custom-inputs">
                            <div className="date-input-wrapper">
                                <Input
                                    label="Fecha inicio"
                                    type="date"
                                    value={dateRange.startDate}
                                    onChange={(value) => handleDateChange('startDate', value)}
                                />
                            </div>
                            <div className="date-input-separator">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="date-input-wrapper">
                                <Input
                                    label="Fecha fin"
                                    type="date"
                                    value={dateRange.endDate}
                                    onChange={(value) => handleDateChange('endDate', value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* KPIs Principales */}
                <div className="statistics-section">
                    <div className="kpi-grid">
                        <div className="kpi-card">
                            <div className="kpi-card-label">Usuarios</div>
                            <div className="kpi-card-value">{usersData.total || 0}</div>
                            <div className="kpi-card-subtitle">
                                {usersData.postulants || 0} postulantes · {usersData.employers || 0} empleadores
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-card-label">Ofertas</div>
                            <div className="kpi-card-value">{jobsData.total || 0}</div>
                            <div className="kpi-card-subtitle">
                                {jobsData.active || 0} activos
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-card-label">Prácticas</div>
                            <div className="kpi-card-value">{practicesData.total || 0}</div>
                            <div className="kpi-card-subtitle">
                                {practicesData.active || 0} activas
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-card-label">Postulaciones</div>
                            <div className="kpi-card-value">{applicationsData.total || 0}</div>
                            <div className="kpi-card-subtitle">
                                {applicationsData.conversionRate || 0}% tasa de conversión
                            </div>
                        </div>
                    </div>
                </div>

                <hr className="divider" />

                {/* Resumen */}
                {summary && (
                    <div className="statistics-section">
                        <h2 className="statistics-section-title">Resumen</h2>
                        <div className="detail-grid">
                            <div className="detail-card">
                                <div className="detail-card-title">Actividad</div>
                                <div className="detail-list">
                                    <div className="detail-item">
                                        <span className="detail-item-label">Oportunidades totales</span>
                                        <span className="detail-item-value">
                                            {(jobsData.total || 0) + (practicesData.total || 0)}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-item-label">Postulaciones activas</span>
                                        <span className="detail-item-value">{applicationsData.active || 0}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-item-label">Tasa de conversión</span>
                                        <span className="detail-item-value">{applicationsData.conversionRate || 0}%</span>
                                    </div>
                                </div>
                            </div>
                            <div className="detail-card">
                                <div className="detail-card-title">Usuarios</div>
                                <div className="detail-list">
                                    <div className="detail-item">
                                        <span className="detail-item-label">Postulantes</span>
                                        <span className="detail-item-value">{usersData.postulants || 0}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-item-label">Empleadores</span>
                                        <span className="detail-item-value">{usersData.employers || 0}</span>
                                    </div>
                                    {/* <div className="detail-item">
                                        <span className="detail-item-label">Docentes</span>
                                        <span className="detail-item-value">{usersData.teachers || 0}</span>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Categorías */}
                {categories && Array.isArray(categories) && categories.length > 0 && (
                    <>
                        <hr className="divider" />
                        <div className="statistics-section">
                            <h2 className="statistics-section-title">Por categoría</h2>
                            <div className="detail-card">
                                <div className="simple-list">
                                    {categories.slice(0, 10).map((category, index) => (
                                        <div key={index} className="simple-list-item">
                                            <span className="simple-list-item-label">
                                                {category.name || category.category || 'Sin categoría'}
                                            </span>
                                            <span className="simple-list-item-value">
                                                {category.count || category.total || 0}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Geografía */}
                {/* {geography && (
                    <>
                        <hr className="divider" />
                        <div className="statistics-section">
                            <h2 className="statistics-section-title">Distribución geográfica</h2>
                            <div className="detail-grid">
                                {Array.isArray(geography.countries) && geography.countries.length > 0 && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Países</div>
                                        <div className="simple-list">
                                            {geography.countries.slice(0, 10).map((item, index) => (
                                                <div key={index} className="simple-list-item">
                                                    <span className="simple-list-item-label">
                                                        {item.country || item.name}
                                                    </span>
                                                    <span className="simple-list-item-value">
                                                        {item.count || item.total || 0}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {Array.isArray(geography.cities) && geography.cities.length > 0 && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Ciudades</div>
                                        <div className="simple-list">
                                            {geography.cities.slice(0, 10).map((item, index) => (
                                                <div key={index} className="simple-list-item">
                                                    <span className="simple-list-item-label">
                                                        {item.city || item.name}
                                                    </span>
                                                    <span className="simple-list-item-value">
                                                        {item.count || item.total || 0}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {(!Array.isArray(geography.countries) || geography.countries.length === 0) &&
                                  (!Array.isArray(geography.cities) || geography.cities.length === 0) && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Sin datos</div>
                                        <div style={{ color: '#6B7280', fontSize: '14px' }}>
                                            No hay datos de ubicación para el rango seleccionado.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )} */}

                {/* Geografía - Compañías */}
                {/* {companyGeography && (
                    <>
                        <hr className="divider" />
                        <div className="statistics-section">
                            <h2 className="statistics-section-title">Distribución geográfica · Compañías</h2>
                            <div className="detail-grid">
                                {Array.isArray(companyGeography.countries) && companyGeography.countries.length > 0 && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Países</div>
                                        <div className="simple-list">
                                            {companyGeography.countries.slice(0, 10).map((item, index) => (
                                                <div key={index} className="simple-list-item">
                                                    <span className="simple-list-item-label">{item.country || item.name}</span>
                                                    <span className="simple-list-item-value">{item.count || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {Array.isArray(companyGeography.cities) && companyGeography.cities.length > 0 && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Ciudades</div>
                                        <div className="simple-list">
                                            {companyGeography.cities.slice(0, 10).map((item, index) => (
                                                <div key={index} className="simple-list-item">
                                                    <span className="simple-list-item-label">{item.city || item.name}</span>
                                                    <span className="simple-list-item-value">{item.count || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {(!companyGeography.countries?.length && !companyGeography.cities?.length) && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Sin datos</div>
                                        <div style={{ color: '#6B7280', fontSize: '14px' }}>
                                            No hay información de ubicación para compañías.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )} */}

                {/* Geografía - Empleadores */}
                {employerGeography && (
                    <>
                        <hr className="divider" />
                        <div className="statistics-section">
                            <h2 className="statistics-section-title">Distribución geográfica · Empleadores</h2>
                            <div className="detail-grid">
                                {Array.isArray(employerGeography.countries) && employerGeography.countries.length > 0 && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Países</div>
                                        <div className="simple-list">
                                            {employerGeography.countries.slice(0, 10).map((item, index) => (
                                                <div key={index} className="simple-list-item">
                                                    <span className="simple-list-item-label">{item.country || item.name}</span>
                                                    <span className="simple-list-item-value">{item.count || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {Array.isArray(employerGeography.cities) && employerGeography.cities.length > 0 && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Ciudades</div>
                                        <div className="simple-list">
                                            {employerGeography.cities.slice(0, 10).map((item, index) => (
                                                <div key={index} className="simple-list-item">
                                                    <span className="simple-list-item-label">{item.city || item.name}</span>
                                                    <span className="simple-list-item-value">{item.count || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {(!employerGeography.countries?.length && !employerGeography.cities?.length) && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Sin datos</div>
                                        <div style={{ color: '#6B7280', fontSize: '14px' }}>
                                            No hay información de ubicación para empleadores.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* Postulaciones */}
                {applicationStats && (
                    <>
                        <hr className="divider" />
                        <div className="statistics-section">
                            <h2 className="statistics-section-title">Postulaciones</h2>
                            <div className="detail-grid">
                                {applicationStats.byStage && Object.entries(applicationStats.byStage).length > 0 && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Por etapa</div>
                                        <div className="simple-list">
                                            {Object.entries(applicationStats.byStage)
                                                .sort((a, b) => b[1] - a[1])
                                                .map(([stage, count]) => (
                                                    <div 
                                                        key={stage} 
                                                        className="simple-list-item"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleViewApplications('stage', stage, translateStage(stage))}
                                                        title="Haz clic para ver aplicaciones"
                                                    >
                                                        <span className="simple-list-item-label">{translateStage(stage)}</span>
                                                        <span className="simple-list-item-value">{count}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}
                                {applicationStats.byStatus && Object.entries(applicationStats.byStatus).length > 0 && (
                                    <div className="detail-card">
                                        <div className="detail-card-title">Por estado</div>
                                        <div className="simple-list">
                                            {Object.entries(applicationStats.byStatus)
                                                .sort((a, b) => b[1] - a[1])
                                                .map(([status, count]) => (
                                                    <div 
                                                        key={status} 
                                                        className="simple-list-item"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleViewApplications('status', status, translateStatus(status))}
                                                        title="Haz clic para ver aplicaciones"
                                                    >
                                                        <span className="simple-list-item-label">{translateStatus(status)}</span>
                                                        <span className="simple-list-item-value">{count}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            
            <Modal
                isOpen={showApplicationsModal}
                onClose={() => setShowApplicationsModal(false)}
                label={modalTitle}
                width="90%"
                height="80vh"
                acceptButtonText="Cerrar"
                cancelButtonText=""
                onAccept={() => setShowApplicationsModal(false)}
                onCancel={() => setShowApplicationsModal(false)}
            >
                {loadingApplications ? (
                    <Loader text="Cargando aplicaciones..." />
                ) : filteredApplications.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>
                        No hay aplicaciones para mostrar
                    </div>
                ) : (
                    <div style={{ 
                        maxHeight: 'calc(80vh - 180px)', 
                        overflowY: 'auto',
                        overflowX: 'auto'
                    }}>
                        <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse',
                            minWidth: '600px'
                        }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 10 }}>
                                <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Usuario</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Email</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: '13px' }}>Oportunidad</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Tipo</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Fecha</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Etapa</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredApplications.map((app, index) => {
                                    // Extraer información del usuario de diferentes formas posibles
                                    const userName = app.user?.name || 
                                                   app.user?.firstName || 
                                                   app.userName || 
                                                   app.postulant?.name ||
                                                   app.postulant?.firstName ||
                                                   app.user?.firstName + ' ' + app.user?.lastName || 
                                                   '-';
                                    const userEmail = app.user?.email || 
                                                    app.userEmail || 
                                                    app.postulant?.email ||
                                                    app.user?.emailAddress ||
                                                    '-';
                                    
                                    // Extraer información de la oportunidad
                                    const opportunityTitle = app.job?.title || 
                                                           app.practice?.title || 
                                                           app.opportunityTitle ||
                                                           app.jobTitle ||
                                                           app.practiceTitle ||
                                                           '-';
                                    const opportunityType = app.job ? 'Trabajo' : app.practice ? 'Práctica' : app.jobId ? 'Trabajo' : app.practiceId ? 'Práctica' : '-';
                                    
                                    return (
                                        <tr 
                                            key={app.id || app._id || index} 
                                            style={{ 
                                                borderBottom: '1px solid #E5E7EB',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <td style={{ padding: '10px 8px', fontSize: '13px' }}>
                                                {userName}
                                            </td>
                                            <td style={{ padding: '10px 8px', fontSize: '13px', color: '#6B7280' }}>
                                                {userEmail}
                                            </td>
                                            <td style={{ padding: '10px 8px', fontSize: '13px' }}>
                                                {opportunityTitle}
                                            </td>
                                            <td style={{ padding: '10px 8px', fontSize: '13px' }}>
                                                {opportunityType}
                                            </td>
                                            <td style={{ padding: '10px 8px', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                {formatApplicationDate(app.createdAt || app.date || app.applicationDate || app.created_at)}
                                            </td>
                                            <td style={{ padding: '10px 8px', fontSize: '13px' }}>
                                                {app.stage && (
                                                    <span 
                                                        style={{ 
                                                            display: 'inline-block',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: getStageColor(app.stage) + '20',
                                                            color: getStageColor(app.stage),
                                                            fontWeight: 500,
                                                            fontSize: '12px',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {translateStage(app.stage)}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '10px 8px', fontSize: '13px' }}>
                                                {app.status && (
                                                    <span 
                                                        style={{ 
                                                            display: 'inline-block',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: getStatusColor(app.status) + '20',
                                                            color: getStatusColor(app.status),
                                                            fontWeight: 500,
                                                            fontSize: '12px',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {translateStatus(app.status)}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>
        </ToolbarComponent>
    );
};

export default StatisticsPage;
